/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 * Notifications module
 *
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.24.0
 */

import { FC, useCallback, useRef, useState } from 'react';

import { Menu } from '@nexign/scc-ui-kit';
import { useSelector } from 'react-redux';

import PageLocale from '../../internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../internationalization/constants';
import { makeSelectUserNotificationsTranslation } from '../../internationalization/intSelectors';
import { ONBOARDING_DATA_ATTR } from '../../onboarding/onboardingConstants';
import { EOnboardingComponentsId } from '../../onboarding/types/onboardingTypes';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { UserNotificationsContainerStyled } from '../styled/UserNotificationsStyled';
import { UserNotificationsButton } from './UserNotificationsButton';
import { NotificationsComponentMemo } from './UserNotificationsComponent';

const UserNotifications: FC = () => {
    const notificationTranslation = useSelector(makeSelectUserNotificationsTranslation());

    const [menuOpen, setMenuOpen] = useState(false);

    const menuAnchorRef = useRef<HTMLButtonElement | null>(null);

    const handleMenuOpen = useCallback(() => setMenuOpen(true), []);
    const handleMenuClose = useCallback(() => setMenuOpen(false), []);

    usePushNotifications();

    return (
        <UserNotificationsContainerStyled {...{ [ONBOARDING_DATA_ATTR]: EOnboardingComponentsId.notificationsMenu }}>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.userNotifications]} />
            <UserNotificationsButton handleMenuOpen={handleMenuOpen} ref={menuAnchorRef} />
            <Menu
                arrow
                withCloseButton
                anchorEl={menuAnchorRef.current}
                disablePortal={false}
                open={menuOpen}
                placement="bottom-end"
                style={{ zIndex: 501 }}
                title={notificationTranslation.menuTitle}
                onClose={handleMenuClose}
            >
                <NotificationsComponentMemo handleMenuClose={handleMenuClose} />
            </Menu>
        </UserNotificationsContainerStyled>
    );
};

export default UserNotifications;
